import { InMemoryCache } from '@apollo/client'
import { translate } from '@translations/TranslationsProvider/TranslationsProvider'
import { BOARD_DRAFT_SECTION_PLACEHOLDER } from '@utils/boards'
import { getLoadedTranslations } from '@utils/i18n'
import { Language } from './__generated__/graphql'
import { GET_USER_ACCOUNT } from './queries/Organization'

const makePaginationMergeFn =
  ({ queryField, getPageRequest, inverted = false }) =>
  (existing, incoming, { args }) => {
    const incomingData = Array.isArray(incoming)
      ? incoming
      : (incoming?.[queryField] ?? [])

    const existingData = existing?.[queryField] ?? existing ?? []

    // Apply inverted logic to reverse the incoming data if needed
    const processedIncomingData = inverted
      ? [...incomingData].reverse()
      : incomingData

    const mergedData = existingData.slice(0)

    const page = getPageRequest(args).page
    const size = getPageRequest(args).size

    // If the page is 0, return only the incoming data
    if (page === 0) {
      if (!Array.isArray(incoming)) {
        return {
          ...incoming,
          [queryField]: processedIncomingData,
        }
      }
      return processedIncomingData
    }

    let startIndex
    if (inverted) {
      startIndex = mergedData.length - (page + 1) * size
    } else {
      startIndex = page * size
    }

    if (inverted && startIndex < 0) {
      mergedData.splice(0, 0, ...processedIncomingData)
    } else {
      for (let i = 0; i < processedIncomingData.length; ++i) {
        const index = startIndex + i
        mergedData[index] = processedIncomingData[i]
      }
    }

    if (!Array.isArray(incoming)) {
      return {
        ...incoming,
        [queryField]: mergedData,
      }
    }

    return mergedData
  }

const readSectionField = function (name = '', { cache }) {
  const response = cache.readQuery({
    query: GET_USER_ACCOUNT,
  })

  const lang = response?.getCurrentUserAccount?.lang ?? Language.En
  return name?.replace(
    BOARD_DRAFT_SECTION_PLACEHOLDER,
    translate('goals.sections.draft', null, {
      translations: getLoadedTranslations(lang),
    }),
  )
}

const takeIncoming = (existing, incoming) => {
  return incoming
}

export const cache: InMemoryCache = new InMemoryCache({
  possibleTypes: {
    NotificationValue: [
      'NotificationValueString',
      'NotificationValueAccounts',
      'NotificationValueTaskDeleted',
      'Timestamp',
    ],
    LogEntryValue: [
      'StringValue',
      'FloatValue',
      'IntValue',
      'LogEntryValueAccounts',
      'TaskDeletedLogEntry',
      'LogEntryValueLabels',
      'Timestamp',
    ],
  },
  typePolicies: {
    Query: {
      fields: {
        getCurrentUserAccount: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming)
          },
        },
        dynamicPlannings: {
          merge: takeIncoming,
        },
        searchContacts: {
          merge: takeIncoming,
        },
        getLoggedTimes: {
          merge: takeIncoming,
        },
        getAccounts: {
          merge: takeIncoming,
        },
        getTeams: {
          merge: takeIncoming,
        },
        getGoal: {
          keyArgs: ['input', 'includeResourcesForGoalTree'],
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming)
          },
        },
        getTask: {
          keyArgs: ['input'],
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming)
          },
        },
        threadCounters: {
          merge: true,
        },
        searchThreads: {
          keyArgs: ['input', ['sorters', 'filters']],
          merge: makePaginationMergeFn({
            queryField: 'threads',
            getPageRequest: (args) => args?.input?.pageRequest,
          }),
        },
        recentThreads: {
          keyArgs: ['unreadOnly'],
          merge: makePaginationMergeFn({
            queryField: 'activities',
            getPageRequest: (args) => args?.pageRequest,
          }),
        },
        allThreadDrafts: {
          merge: takeIncoming,
        },
        searchBoards: {
          keyArgs: ['input', ['sorters', 'filters']],
          merge: makePaginationMergeFn({
            queryField: 'boards',
            getPageRequest: (args) => args?.input?.pageRequest,
          }),
        },
        searchFashionCollections: {
          keyArgs: ['input', ['sorters', 'filters']],
          merge: makePaginationMergeFn({
            queryField: 'fashionCollections',
            getPageRequest: (args) => args?.input?.pageRequest,
          }),
        },
      },
    },
    Account: {
      merge: true,
      fields: {
        calendars: {
          merge: takeIncoming,
        },
      },
    },
    EmailAccount: {
      fields: {
        otherAccounts: {
          merge: takeIncoming,
        },
      },
    },
    Project: {
      fields: {
        assignedProducers: {
          merge: takeIncoming,
        },
      },
    },
    Section: {
      fields: {
        name: {
          read: readSectionField,
        },
        goals: {
          merge: takeIncoming,
        },
      },
    },
    FashionCollectionSection: {
      fields: {
        name: {
          read: readSectionField,
        },
        goals: {
          merge: takeIncoming,
        },
      },
    },
    Milestone: {
      fields: {
        tasks: {
          merge: takeIncoming,
        },
      },
    },
    Task: {
      fields: {
        assignees: {
          merge: takeIncoming,
        },
        followers: {
          merge: takeIncoming,
        },
        taskTree: {
          merge(existing, incoming) {
            if (existing && !incoming) {
              return existing
            }

            return incoming
          },
        },
      },
    },
    RecurringTaskDefinition: {
      fields: {
        followers: {
          merge: takeIncoming,
        },
      },
    },
    Board: {
      fields: {
        sections: {
          merge: takeIncoming,
        },
      },
    },
    Goal: {
      fields: {
        resources: {
          merge: takeIncoming,
        },
        labels: {
          merge: takeIncoming,
        },
        comments: {
          merge: takeIncoming,
        },
        followers: {
          merge: takeIncoming,
        },
        commentsState: {
          merge: true,
        },
        goalTree: {
          merge: takeIncoming,
        },
        boards: {
          merge: takeIncoming,
        },
        sections: {
          merge: takeIncoming,
        },
        projects: {
          merge: takeIncoming,
        },
      },
    },
    Thread: {
      fields: {
        comments: {
          keyArgs: false,
          merge: makePaginationMergeFn({
            queryField: '',
            getPageRequest: (args) => args?.pageRequest,
            inverted: true,
          }),
        },
      },
    },
    ThreadCommentActivity: {
      fields: {
        time: {
          merge: takeIncoming,
        },
      },
    },
    ThreadDraft: {
      fields: {
        labels: {
          merge: takeIncoming,
        },
      },
    },
    Comment: {
      fields: {
        recipients: {
          merge: takeIncoming,
        },
        likeDetails: {
          merge: takeIncoming,
        },
      },
    },
    ProcedureMilestone: {
      fields: {
        milestones: {
          merge: takeIncoming,
        },
      },
    },
    Team: {
      fields: {
        members: {
          merge: takeIncoming,
        },
      },
    },
    Counters: {
      keyFields: ['accountId'],
      fields: {
        tasks: {
          merge(existing, incoming) {
            return {
              ...existing,
              ...incoming,
            }
          },
        },
        todos: {
          merge(existing, incoming) {
            return {
              ...existing,
              ...incoming,
            }
          },
        },
      },
    },
    DynamicPlanning: {
      keyFields: ({ goal, milestone, task, project, thread }) =>
        (goal as any)?.__typename ||
        (milestone as any)?.__typename ||
        (task as any)?.__typename ||
        (project as any)?.__typename ||
        (thread as any)?.__typename,
    },
  },
})
